import type { FC, PointerEvent } from 'react'
import type { GameInfo } from '@/types'
import type { SceneProps } from '@/components/landing/games/scene-ui'

import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { demoUrl } from '@/constants/kinematic'

interface GameTileProps {
    game: GameInfo
    index: number
    total: number
    Scene: FC<SceneProps>
    className?: string
    stageClassName?: string
}

const GameTile: FC<GameTileProps> = ({ game, index, total, Scene, className, stageClassName }) => {
    const reduced = useReducedMotion()
    const { ref, inView } = useInView<HTMLElement>('160px')

    const trackPointer = (event: PointerEvent<HTMLElement>) => {
        const rect = event.currentTarget.getBoundingClientRect()
        event.currentTarget.style.setProperty('--mx', `${event.clientX - rect.left}px`)
        event.currentTarget.style.setProperty('--my', `${event.clientY - rect.top}px`)
    }

    return (
        <article
            ref={ref}
            onPointerMove={trackPointer}
            aria-labelledby={`game-${game.id}`}
            className={cn(
                'group relative flex flex-col overflow-hidden rounded-[26px] border border-white/[0.07] bg-ink-1 transition-colors duration-500 hover:border-white/[0.15]',
                className
            )}
        >
            <div className={cn('@container relative flex-1 overflow-hidden bg-[radial-gradient(120%_90%_at_50%_0%,#17171c_0%,#0b0b0d_62%)]', stageClassName)}>
                <div aria-hidden='true' className='dot-field pointer-events-none absolute inset-0 opacity-50 [mask-image:radial-gradient(ellipse_at_center,black,transparent_72%)]' />

                <Scene active={inView && !reduced} />

                <div className='pointer-events-none absolute inset-x-4 top-4 z-10 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.16em]'>
                    <span className='text-steel'>
                        {pad(index)}
                        <span className='text-steel/45'> / {total}</span>
                    </span>
                    <span className='rounded-full border border-white/[0.08] bg-ink/70 px-2 py-0.5 text-steel backdrop-blur'>
                        Max <span className='text-bone'>×{game.max}</span>
                    </span>
                </div>
            </div>

            <div aria-hidden='true' className='spotlight pointer-events-none absolute inset-0 z-20 opacity-0 transition-opacity duration-500 group-hover:opacity-100' />

            <div className='relative z-30 flex items-end justify-between gap-5 border-t border-white/[0.06] p-5 md:p-6'>
                <div className='min-w-0'>
                    <div className='flex flex-wrap items-baseline gap-x-3 gap-y-1'>
                        <h3 id={`game-${game.id}`} className='text-[22px] font-semibold leading-none tracking-[-0.02em] text-bone stretch-semi'>
                            {game.name}
                        </h3>
                        <span className='font-mono text-[10px] uppercase tracking-[0.14em] text-steel'>
                            {game.spec}
                        </span>
                    </div>

                    <p className='mt-2 max-w-[48ch] text-sm leading-relaxed text-steel'>
                        {game.description}
                    </p>
                </div>

                <a
                    href={demoUrl(game.id)}
                    target='_blank'
                    rel='noreferrer'
                    className='grid size-11 shrink-0 place-items-center rounded-full border border-white/10 text-bone transition-all duration-300 hover:border-ember hover:bg-ember hover:text-ink group-hover:border-ember/60'
                >
                    <ArrowUpRight aria-hidden='true' className='size-4' />
                    <span className='sr-only'>Play the {game.name} demo (opens in a new tab)</span>
                </a>
            </div>
        </article>
    )
}

export default GameTile
